import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSite } from '../hooks/useSites';
import { useLoggers } from '../hooks/useLoggers';
import { Logger } from '../services/data';
import Card from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';
import StatusBadge from '../components/ui/StatusBadge';
import Button from '../components/ui/Button';

export default function SiteDetail() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const { data: site, isLoading: siteLoading, error: siteError } = useSite(siteId);
  const { data: loggers = [], isLoading: loggersLoading } = useLoggers(siteId);

  if (siteLoading || loggersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (siteError || !site) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('common.error')}</h1>
        <Card>
          <p className="text-red-600">{t('common.error')}</p>
        </Card>
      </div>
    );
  }

  const getLoggerStatus = (logger: Logger) => {
    if (!logger.is_active) return 'offline';

    const lastSeenDate = logger.last_seen_at ? new Date(logger.last_seen_at) : null;
    if (!lastSeenDate) return 'unknown';

    const hoursSinceLastSeen = (Date.now() - lastSeenDate.getTime()) / (1000 * 60 * 60);
    if (hoursSinceLastSeen > 24) return 'offline';
    if (hoursSinceLastSeen > 2) return 'warning';

    return 'ok';
  };

  const formatLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return t('common.never');

    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins} min`;
    } else if (diffHours < 24) {
      return `${diffHours}h`;
    } else {
      return `${diffDays}j`;
    }
  };

  return (
    <div>
      <div className="mb-6">
        <Link to="/sites">
          <Button variant="secondary" size="sm">
            ← {t('common.backToList')}
          </Button>
        </Link>
      </div>

      <Card className="mb-6">
        <div className="space-y-4">
          <h1 className="text-3xl font-bold text-gray-900">{site.name}</h1>

          {site.address && (
            <p className="text-gray-600">
              <span className="font-medium">{t('site.address')}:</span> {site.address}
            </p>
          )}

          <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
            <span className="text-sm text-gray-600">
              {t('site.loggersCount')}: <span className="font-semibold text-gray-900">{loggers.length}</span>
            </span>
            <span className={`text-xs ${site.is_active ? 'text-green-600' : 'text-gray-400'}`}>
              {site.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </Card>

      <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('site.loggersList')}</h2>

      {loggers.length === 0 ? (
        <Card>
          <p className="text-gray-500 text-center py-8">{t('site.noLoggers')}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loggers.map((logger) => (
            <Link key={logger.id} to={`/loggers/${logger.id}`}>
              <Card className="hover:shadow-lg transition-shadow h-full">
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{logger.name}</h3>
                    <StatusBadge status={getLoggerStatus(logger)} />
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">{t('logger.serialNumber')}:</span> {logger.serial_number}
                    </p>

                    {logger.battery_level !== undefined && logger.battery_level !== null && (
                      <div>
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                          <span className="font-medium">{t('logger.battery')}:</span>
                          <span>{logger.battery_level}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              logger.battery_level > 50
                                ? 'bg-green-500'
                                : logger.battery_level > 20
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                            }`}
                            style={{ width: `${logger.battery_level}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <p className="text-sm text-gray-600">
                      <span className="font-medium">{t('logger.lastSeen')}:</span>{' '}
                      {formatLastSeen(logger.last_seen_at)}
                    </p>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
